/**
 * The only figures this screen derives itself: the balance of a group from its
 * stance cells, the coverage of the composition by the comments route, the
 * ranking of groups by balance, specificity and size, the order of the
 * amplifier list, the stance bar's segment widths, and the narrowing of a
 * composition against the comments on screen (platform design §17.10).
 * Everything else arrives per row.
 */

import type { AmplificationRow, Composition, PostMetricRow, StanceCounts } from '../api/schemas';

/** The balance axis is given, never fitted to the data, so two posts stay
 *  comparable. */
export const BALANCE_DOMAIN = [-100, 100] as const;

/** The stored dimensions the screen reads, one chart each. Income is not one:
 *  an income band inferred from an avatar and a bio is a guess about people's
 *  money (§10.4). */
export const DIMENSIONS = ['age_band', 'gender_presentation', 'occupation', 'education'] as const;
export type Dimension = (typeof DIMENSIONS)[number];

export interface Group {
  key: string;
  dimensions: readonly Dimension[];
  /** Each dimension's vocabulary label, already resolved by `labelValue` and
   *  joined in `dimensions` order -- never the machine token a composition
   *  cell carries on the wire. */
  value: string;
  /** Null once the screen withholds the population these count within: a group
   *  count is an addend of it, not a figure of its own. */
  n: number | null;
  balance: number | null;
}

/**
 * Every member of every closed vocabulary `profile_account.VOCABULARIES`
 * declares (core, `llm/prompts/profile_account.py`), labelled for a
 * campaign's own communications team -- not the core engineer who named the
 * token. Only four of these seven are wired to a screen dimension today
 * (`DIMENSIONS` above); the other three are labelled anyway because the
 * vocabulary, not the screen, is what this map promises to cover.
 */
const VOCABULARY_LABELS: Record<string, Record<string, string>> = {
  // The ontology baseline's bands (core's `ontology/baseline.py`, `faixa_etaria`).
  age_band: {
    '18-24': '18 a 24 anos',
    '25-34': '25 a 34 anos',
    '35-44': '35 a 44 anos',
    '45-59': '45 a 59 anos',
    '60-69': '60 a 69 anos',
    '70+': '70 anos ou mais',
    // The ontology's own terms (core's `ontology/baseline.py`), already words.
    'Menor de 18': 'Menor de 18',
    'Não identificado': 'Não identificado',
  },
  gender_presentation: {
    feminine: 'Feminino',
    masculine: 'Masculino',
    androgynous: 'Andrógino',
  },
  relationship_status: {
    single: 'Solteiro(a)',
    in_a_relationship: 'Namorando',
    engaged: 'Noivo(a)',
    married: 'Casado(a)',
    divorced: 'Divorciado(a)',
    widowed: 'Viúvo(a)',
  },
  parenthood: {
    parent: 'Tem filhos',
  },
  occupation: {
    managers: 'Gestão',
    professionals: 'Profissionais liberais',
    technicians: 'Técnicos',
    clerical_support: 'Administrativo',
    service_and_sales: 'Serviços e vendas',
    agricultural: 'Agropecuária',
    craft_and_trades: 'Ofícios manuais',
    machine_operators: 'Operação de máquinas',
    elementary: 'Ocupações elementares',
    armed_forces: 'Forças armadas',
    student: 'Estudante',
    retired: 'Aposentado(a)',
    homemaker: 'Do lar',
  },
  education: {
    primary: 'Fundamental',
    secondary: 'Médio',
    technical: 'Técnico',
    higher: 'Superior',
    postgraduate: 'Pós-graduação',
  },
};

/**
 * A composition cell's value as a pt-BR reader can read it. A value this map
 * has not caught up to yet -- a future core vocabulary member -- still reads
 * as words: its underscores become spaces rather than the cell rendering a
 * machine token or nothing at all.
 *
 * ponytail: no automatic catch-up when core adds a vocabulary member; extend
 * `VOCABULARY_LABELS` by hand when it does.
 */
export function labelValue(dimension: string, value: string): string {
  return VOCABULARY_LABELS[dimension]?.[value] ?? value.replace(/_/g, ' ');
}

/** A vocabulary value's label when exactly one dimension holds it, for a caller that has the
 *  value but not a trustworthy dimension name (a column the assistant's query renamed). */
export function labelVocabularyValue(value: string): string | undefined {
  const labels = Object.values(VOCABULARY_LABELS).flatMap((values) =>
    Object.hasOwn(values, value) ? [values[value]] : [],
  );
  return labels.length === 1 ? labels[0] : undefined;
}

export type Reading =
  | { kind: 'not-collected'; total: number | null }
  | {
      kind: 'read';
      groups: Group[];
      suppressedShare: number;
      narrowed: boolean;
      total: number | null;
    };

/**
 * A cell within one of the comments at its stance lets a reader join the list
 * to the cell by eye: five hostile women aged 26 to 34 beside six hostile
 * comments.
 *
 * The basis is the post's whole comment population at that stance, never the
 * rows currently rendered. A basis that moves with what is on screen is a
 * channel of its own: the reader pages, or re-reads under another window, and
 * reads a withheld cell's count off the state it vanished at. The population
 * is on the screen anyway — the list states its four stance counts above the
 * comments it has loaded.
 *
 * A stance the counts do not carry cannot be compared, so it is withheld.
 */
export function narrows(cellCount: number, cellStance: string, counts: StanceCounts): boolean {
  if (!Object.hasOwn(counts, cellStance)) return true;
  return Math.abs(cellCount - counts[cellStance as keyof StanceCounts]) <= 1;
}

function groupKey(attributes: Record<string, string>): string {
  return Object.entries(attributes)
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([name, value]) => `${name}=${value}`)
    .join('|');
}

/**
 * One dimension set's composition as balances, narrowed against the post's
 * comments. A set of several dimensions reads joint groups, one per
 * combination of values the gate returned.
 *
 * A group with any narrowed cell is withheld whole: its balance from the
 * cells that remain would be wrong, and the withheld cell would be the
 * difference. Withholding also forfeits `total`, for the same reason the gate
 * withholds it once anything is dropped.
 *
 * The narrowed share weighs people, not cells: a share of cells calls a
 * five-person cell and a five-hundred-person cell the same loss and
 * understates whenever the withheld cells are the large ones. Weighing people
 * also survives a gate that merged several cells into one, because merging
 * moves no people. It is still stated as a floor, since `suppressed_share`
 * has no producer to pin its own unit to.
 *
 * Narrowing is the gate's own rule applied to what the screen can join by eye,
 * so it runs only while the gate does: with both thresholds switched off in
 * Ops nothing is narrowed, and a cell with no value for a dimension is simply
 * not measured on it rather than withheld.
 */
export function readComposition(
  dimension: Dimension | readonly Dimension[],
  composition: Composition,
  counts: StanceCounts,
): Reading {
  const dimensions: readonly Dimension[] = typeof dimension === 'string' ? [dimension] : dimension;
  const { cells } = composition;
  const gated = composition.k.default > 0 || composition.k.sensitive > 0;
  if (composition.total === 0 || (cells.length === 0 && composition.suppressed_share === 0)) {
    return { kind: 'not-collected', total: composition.total };
  }

  const byGroup = new Map<string, typeof cells>();
  for (const cell of cells) {
    const key = groupKey(cell.attributes);
    byGroup.set(key, [...(byGroup.get(key) ?? []), cell]);
  }

  const groups: Group[] = [];
  let keptCells = 0;
  let keptPeople = 0;
  let unmeasuredCells = 0;
  for (const [key, groupCells] of byGroup) {
    const attributes = groupCells[0]?.attributes;
    const values = dimensions.map((name) => attributes?.[name]);
    if (values.some((value) => value === undefined)) {
      if (!gated) unmeasuredCells += groupCells.length;
      continue;
    }
    if (gated && groupCells.some((cell) => narrows(cell.count, cell.stance, counts))) continue;

    keptCells += groupCells.length;
    const n = groupCells.reduce((sum, cell) => sum + cell.count, 0);
    keptPeople += n;
    const of = (stance: string) =>
      groupCells
        .filter((cell) => cell.stance === stance)
        .reduce((sum, cell) => sum + cell.count, 0);
    groups.push({
      key,
      dimensions,
      value: dimensions.map((name, index) => labelValue(name, values[index] ?? '')).join(' · '),
      n,
      balance: n === 0 ? null : Math.round((100 * (of('supportive') - of('hostile'))) / n),
    });
  }

  const returned = cells.reduce((sum, cell) => sum + cell.count, 0);
  const narrowed = keptCells + unmeasuredCells < cells.length;
  return {
    kind: 'read',
    groups,
    narrowed,
    suppressedShare: narrowed
      ? 1 - (1 - composition.suppressed_share) * (returned === 0 ? 0 : keptPeople / returned)
      : composition.suppressed_share,
    total: narrowed ? null : composition.total,
  };
}

/**
 * The dimensions disclose as one screen, not as three cards.
 *
 * `total` counts one population, so one dimension's narrowing forfeits it for
 * all of them. Every group count is an addend of that same total, and two
 * arithmetics put it back: a dimension that dropped nothing has rows summing
 * to the population outright, and any dimension's rows give
 * `total = kept / (1 - the share this screen states)`, dropped or not. So no
 * group count is drawn beside a withheld total. Withholding a figure means
 * withholding every input a reader recomputes it from, across the screen
 * rather than within the card that withheld it (§4.5).
 */
function withhold(readings: readonly (Reading | undefined)[]): (Reading | undefined)[] {
  if (!readings.some((reading) => reading?.total === null)) return [...readings];
  return readings.map((reading) => {
    if (reading === undefined) return undefined;
    if (reading.kind !== 'read') return { ...reading, total: null };
    return {
      ...reading,
      total: null,
      groups: reading.groups.map((group) => ({ ...group, n: null })),
    };
  });
}

export type Coverage =
  | { kind: 'unread' }
  | { kind: 'not-collected' }
  | { kind: 'withheld' }
  | { kind: 'read'; n: number; m: number };

/** `N de M com grupo estimado`.
 *
 *  `M` is never the sum of `cells[].count`: platform design §4.5 forbids
 *  rebuilding a suppressed total by adding up the cells that survived, which
 *  under-counts by exactly the number this figure exists to disclose. The
 *  subtraction runs the other way too, so `M` is read off the narrowed
 *  readings rather than the raw payloads: a disclosed `M` beside the groups
 *  this screen still draws hands the reader `M` minus their `n`, which is the
 *  withheld group's own size. One dimension narrowing withholds the figure for
 *  all of them, since every dimension partitions the same population.
 *
 *  `N` is the four stance counts of the same comment population the cells are
 *  narrowed against, so the two sides of the figure count one thing.
 *
 *  A total of zero states no coverage at all: the dimensions below read it as
 *  not collected, and a figure beside them would print an `N` larger than the
 *  `M` it is supposed to be a subset of. `unread` is a state of its own: a
 *  missing payload is not the gate withholding anything.
 *
 *  Any dimension's total answers for all of them, because these readings have
 *  been through `withhold`: one narrowing forfeits the population everywhere,
 *  so the first reading's `total` is null exactly when some reading's is. */
function coverage(readings: readonly (Reading | undefined)[], counts: StanceCounts): Coverage {
  const read = readings.filter((reading): reading is Reading => reading !== undefined);
  const first = read[0];
  if (first === undefined) return { kind: 'unread' };
  const m = first.total;
  if (m === null) return { kind: 'withheld' };
  if (m === 0) return { kind: 'not-collected' };
  return {
    kind: 'read',
    n: counts.supportive + counts.hostile + counts.neutral + counts.off_topic,
    m,
  };
}

/** The narrowing basis as the disclosure reads it: the whole-post stance
 *  counts, or the reason there are none yet. */
export type BasisState =
  { kind: 'pending' } | { kind: 'failed' } | { kind: 'ready'; counts: StanceCounts };

/** One dimension set's `get_composition` query, answered or not. */
export interface DimensionQuery {
  dimension: Dimension | readonly Dimension[];
  status: 'pending' | 'error' | 'success';
  composition: Composition | undefined;
}

/** Everything the screen may draw of its audience, and nothing it may not. */
export type Disclosure =
  | { kind: 'asking' }
  | { kind: 'no-basis' }
  | { kind: 'disclosed'; readings: readonly (Reading | undefined)[]; coverage: Coverage };

/**
 * What this screen may disclose, from everything it knows: the basis and
 * every dimension set's query, answered or not.
 *
 * Disclosure is a property of the screen's whole state, so it is decided once,
 * here, rather than re-derived per card. A joint group's count is an addend
 * of the same population, so the joint readings are settled with the single
 * ones rather than beside them. Two leaks came out of deriving it per
 * card: a card nulling its own total beside a sibling still printing the rows
 * that sum to it, then a card answering first and printing the population its
 * slower sibling was about to withhold. The second is invisible to the
 * readings alone — a dimension still being asked for reads as `undefined`
 * here, never as `null` — which is why this takes the queries' own states and
 * not just their data.
 *
 * `asking` and `no-basis` carry no readings and no coverage, so a figure added
 * to this screen has nothing to draw in either state unless someone goes
 * around this function. An errored dimension is a third case and not one of
 * these two: it never answers, and a dimension the screen never draws
 * withholds nothing anyone could recover, so the other two still disclose.
 */
export function settleDisclosure(
  basis: BasisState,
  dimensions: readonly DimensionQuery[],
): Disclosure {
  if (basis.kind === 'pending') return { kind: 'asking' };
  if (dimensions.some((dimension) => dimension.status === 'pending')) return { kind: 'asking' };
  if (basis.kind === 'failed') return { kind: 'no-basis' };

  const readings = withhold(
    dimensions.map(({ dimension, status, composition }) =>
      status === 'success' && composition !== undefined
        ? readComposition(dimension, composition, basis.counts)
        : undefined,
    ),
  );
  return { kind: 'disclosed', readings, coverage: coverage(readings, basis.counts) };
}

const AMPLIFIERS_SHOWN = 5;

/** This post's amplifiers by reach, largest first; an amplifier with no reach
 *  reading sorts after every one that has one, newest first among those. */
export function topAmplifiers(
  rows: readonly AmplificationRow[],
  postId: string,
): AmplificationRow[] {
  return rows
    .filter((row) => row.source_post_id === postId)
    .sort((a, b) => {
      if (a.reach !== null && b.reach !== null && a.reach !== b.reach) return b.reach - a.reach;
      if (a.reach !== null && b.reach === null) return -1;
      if (a.reach === null && b.reach !== null) return 1;
      return Date.parse(b.observed_at) - Date.parse(a.observed_at);
    })
    .slice(0, AMPLIFIERS_SHOWN);
}

/** The stances the bar draws, in the frame's order. Off-topic comments are
 *  left out of the bar and its legend: they say nothing for or against. */
export const BAR_STANCES = ['supportive', 'neutral', 'hostile'] as const;
export type BarStance = (typeof BAR_STANCES)[number];

/** Each drawn stance's share of the drawn comments, 0..1; all zero when there
 *  are none. */
export function stanceShares(counts: StanceCounts): Record<BarStance, number> {
  const total = BAR_STANCES.reduce((sum, stance) => sum + counts[stance], 0);
  const of = (stance: BarStance) => (total === 0 ? 0 : counts[stance] / total);
  return { supportive: of('supportive'), neutral: of('neutral'), hostile: of('hostile') };
}

const METRIC_ORDER: readonly PostMetricRow['metric'][] = [
  'views',
  'likes',
  'comments',
  'shares',
  'saves',
];

/** The latest observation of each metric per provider: two providers reporting
 *  one metric are two readings, never one. */
export function latestMetrics(rows: readonly PostMetricRow[]): PostMetricRow[] {
  const latest = new Map<string, PostMetricRow>();
  for (const row of rows) {
    const key = `${row.metric}|${row.provider}`;
    const held = latest.get(key);
    if (held === undefined || Date.parse(row.observed_at) > Date.parse(held.observed_at)) {
      latest.set(key, row);
    }
  }
  return [...latest.values()].sort(
    (a, b) =>
      METRIC_ORDER.indexOf(a.metric) - METRIC_ORDER.indexOf(b.metric) ||
      a.provider.localeCompare(b.provider),
  );
}
